import buildService, { NoParams } from 'common/utils/build-service'
import { GET } from 'common/requests'
import { Collection, GlossaryItem, Resource, SearchParams, SearchResource } from './types'

export const getGlossaryService = buildService<NoParams, GlossaryItem, unknown, GlossaryItem>({
  type: GET,
  constantsPrefix: 'GLOSSARY',
  isArray: true,
  http: {
    createApiUrlSuffix: () => `/glossary_items`,
  },
})

export const getCollectionsService = buildService<NoParams, Collection, unknown, Collection>({
  type: GET,
  constantsPrefix: 'COLLECTIONS',
  isArray: true,
  http: {
    createApiUrlSuffix: () => `/all_collections?order=category_weight.asc,weight.asc`,
  },
})

export const getFavoritesService = buildService<NoParams, Resource, unknown, Resource>({
  type: GET,
  constantsPrefix: 'RESOURCE_FAVORITES',
  isArray: true,
  http: {
    createApiUrlSuffix: () => `/my_favorite_resources`,
  },
})

export const getCollectionDetailsService = buildService<
  string,
  Collection & { categories?: { name: string } }
>({
  type: GET,
  constantsPrefix: 'COLLECTION_DETAILS',
  http: {
    createApiUrlSuffix: (id) => `/collections?slug=eq.${id}&select=*,categories(name)`,
    headers: {
      Accept: 'application/vnd.pgrst.object+json',
    },
  },
})

export const getResourceDetailsService = buildService<
  string,
  Resource & {
    collection_slug: string
    help_image_url?: string
    help_image_cap?: string
    sidebar_links?: Array<{ title: string; url: string }>
    suggested_time?: string
  }
>({
  type: GET,
  constantsPrefix: 'RESOURCE_DETAILS',
  http: {
    createApiUrlSuffix: (id) => `/resources?slug=eq.${id}`,
    headers: {
      Accept: 'application/vnd.pgrst.object+json',
    },
  },
})

export const getAllResourcesService = buildService<NoParams, Resource, unknown, Resource>({
  type: GET,
  constantsPrefix: 'ALL_RESOURCES',
  isArray: true,
  http: {
    createApiUrlSuffix: () =>
      `/resources?status=eq.published&select=id,name,teaser,slug,collection_slug,collection_id,is_favorite,new,translations,created_at,resource_type,updated_at,status`,
  },
})

export const searchResourcesService = buildService<
  SearchParams,
  SearchResource,
  unknown,
  SearchResource
>({
  type: GET,
  constantsPrefix: 'SEARCH_RESOURCES',
  isArray: true,
  http: {
    createApiUrlSuffix: ({ searchString, limit }) => {
      const trimmed = `${searchString || ''}`.trim()
      const hasWhitespace = /\s/.test(trimmed)

      // If the search contains spaces, use phrase full-text search (phfts) on the whole phrase.
      // Otherwise, use standard full-text search (fts) on the single term.
      const encodedQuoted = (value: string) => {
        const escaped = value.replace(/\"/g, '\\"')
        const encoded = encodeURIComponent(escaped)
        // Wrap in URL-encoded double quotes to protect commas and special chars in PostgREST logic trees
        return `%22${encoded}%22`
      }

      searchString = hasWhitespace
        ? `&or=(name.phfts.${encodedQuoted(trimmed)},teaser.phfts.${encodedQuoted(
            trimmed,
          )},content.phfts.${encodedQuoted(trimmed)})`
        : `&or=(name.fts.${encodedQuoted(trimmed)},teaser.fts.${encodedQuoted(
            trimmed,
          )},content.fts.${encodedQuoted(trimmed)})`
      const limitString = limit ? `&limit=${limit}` : ''
      return `/available_resources?select=name,slug,collection_name,teaser,content${searchString}${limitString}`
    },
  },
})

export const getCollectionResourcesService = buildService<string, Resource>({
  type: GET,
  constantsPrefix: 'COLLECTION_RESOURCES',
  isArray: true,
  http: {
    createApiUrlSuffix: (id) =>
      `/resources_in_collection?status=eq.published&collection_slug=eq.${id}&order=weight.asc`,
  },
})

export const postVisitResourceService = buildService({
  type: 'POST',
  constantsPrefix: 'RESOURCE_VISIT',
  http: {
    createApiUrlSuffix: () => '/rpc/visit',
    headers: {
      Prefer: 'params=single-object',
    },
  },
})

export const SET_RESOURCE_DETAILS_FAVORITE = 'SET_RESOURCE_DETAILS_FAVORITE'

export const setResourceDetailsFavorite = (resourceId: string, isFavorite: boolean) => ({
  type: SET_RESOURCE_DETAILS_FAVORITE,
  payload: { resourceId, isFavorite },
})

export const resourceDetailsReducer = (
  state: unknown,
  action: { type?: string; payload?: { resourceId?: string; isFavorite?: boolean } },
) => {
  const nextState = getResourceDetailsService.reducer(state as never, action as never)
  if (
    action.type !== SET_RESOURCE_DETAILS_FAVORITE ||
    !action.payload?.resourceId ||
    nextState.item?.id !== action.payload.resourceId
  ) {
    return nextState
  }

  return {
    ...nextState,
    item: {
      ...nextState.item,
      is_favorite: action.payload.isFavorite,
    },
  }
}

export const postFavoriteService = buildService({
  type: 'POST',
  constantsPrefix: 'RESOURCE_FAVORITE',
  http: {
    createApiUrlSuffix: () => '/rpc/favorite',
    headers: {
      Prefer: 'params=single-object',
    },
  },
})
